import {
  DetailedResource,
  getResourceId,
  getResourceName,
  isAPIDetail,
  isARMApplicationDetail,
  isARMHybridDetail,
  isAsset,
  isManagedAPI,
  isRTFApplication,
  isTarget,
  Resource
} from '../../common/resource/model'
import { TargetType } from '@mulesoft/monitoring-center-components/lib/models/resource/hybrid'

export enum Urls {
  modernMonitoring = '/monitoring-new',
  tracesOverview = '/monitoring-new/organizations/:orgId/traces-overview',
  telemetryExporter = '/monitoring-new/organizations/:orgId/telemetry-exporter',
  builtIn = '/builtin',
  customDashboardPage = '/dashboard',
  newCustomDashboard = '/dashboard/new',
  alerts = '/alerts',
  logs = '/logs',
  logPoints = '/log-points',
  apitesting = '/apitesting/ui',
  rawData = '/raw-data',
  tools = '/tools',
  customMetrics = '/custom-metrics',
  reports = '/reports',
  settings = '/settings',
  analyticsLandingPage = '/api-metrics-welcome',
  emptyStatePage = '/empty-state'
}

export const builtInUrl = (envId: string, resourceType: string, id: string, dashboardType: string): string => {
  return `${Urls.builtIn}/${envId}/${resourceType}/${id}/${dashboardType}`
}

export const builtInAppUrl = (envId: string, id: string, dashboardType: string): string => {
  return builtInUrl(envId, 'app', id, dashboardType)
}

export const builtInRTFUrl = (envId: string, id: string, dashboardType: string): string => {
  return builtInUrl(envId, 'mc', id, dashboardType)
}

export const builtInHybridUrl = (envId: string, id: string, target: number, dashboardType: string, resourceType?: string): string => {
  return `${Urls.builtIn}/${envId}/${resourceType ? resourceType : 'hybrid'}/${id}/${target}/${dashboardType}`
}

export const builtInTargetUrl = (envId: string, target: number, dashboardType: string, resourceType: string): string => {
  return `${Urls.builtIn}/${envId}/${resourceType}/${target}/${dashboardType}`
}

export const builtInAPIUrl = (envId: string, apiId: string | number, versionId: string | number, dashboardType: string | number): string => {
  return `${Urls.builtIn}/${envId}/api/${apiId}/version/${versionId}/${dashboardType}`
}

export const builtInManagedAPIUrl = (envId: string, apiId: string | number, dashboardType: string): string => {
  return builtInUrl(envId, 'mapi', apiId.toString(), dashboardType)
}

export const goToBuiltIn = (envId: string, dashboardType: string, resource: Resource, versionId?: string) => {
  if (isARMApplicationDetail(resource)) {
    window.location.hash = builtInAppUrl(envId, getResourceId(resource), dashboardType)
  } else if (isARMHybridDetail(resource)) {
    const targetId = resource.target.id
    window.location.hash = builtInHybridUrl(envId, getResourceName(resource), targetId, dashboardType, 'hybrid')
  } else if (isTarget(resource)) {
    const targetType = resource.type.toLowerCase()
    const targetId = Number(resource.id)
    window.location.hash = builtInTargetUrl(envId, targetId, dashboardType, targetType)
  } else if (isRTFApplication(resource)) {
    window.location.hash = builtInRTFUrl(envId, getResourceId(resource), dashboardType)
  } else if (isAsset(resource) && versionId) {
    window.location.hash = builtInAPIUrl(envId, getResourceId(resource), versionId, dashboardType)
  } else if (isManagedAPI(resource) && !versionId) {
    window.location.hash = builtInManagedAPIUrl(envId, getResourceId(resource), dashboardType)
  }
}

export const getBuiltInURL = (envId: string, dashboardType: string, resourceId: string, rsType: string,
                              targetId?: number, versionId?: string | number) => {
  switch (rsType) {
    case 'cloudhub':
    case 'app':
      return builtInAppUrl(envId, resourceId, dashboardType)
    case 'mc':
      return builtInRTFUrl(envId, resourceId, dashboardType)
    case 'hybrid':
    case 'server':
    case 'server_group':
    case 'cluster':
      return targetId ? builtInHybridUrl(envId, resourceId, targetId, dashboardType) : builtInUrl(envId, rsType, resourceId, dashboardType)
    case 'api':
      return versionId ? builtInAPIUrl(envId, resourceId, versionId, dashboardType) : builtInUrl(envId, rsType, resourceId, dashboardType)
    default:
      return builtInUrl(envId, rsType, resourceId, dashboardType)
  }
}

export const builtInDetailUrl = (resource: DetailedResource | undefined, orgDomain: string, orgId: string, envId: string): string => {
  if (!resource) {
    return ''
  }
  if (isARMApplicationDetail(resource)) {
    return `/cloudhub/#/console/applications/cloudhub/${resource.domain}/settings`
  }
  if (isARMHybridDetail(resource)) {
    return `/cloudhub/#/console/applications/hybrid/${resource.id}/settings`
  }
  if (isRTFApplication(resource)) {
    return `/cloudhub/#/console/applications/runtimeFabric/${resource.id}/settings`
  }
  if (isManagedAPI(resource)) {
    return `/apimanager/${orgDomain}/#/organizations/${orgId}/environments/${envId}/managedServiceApis/${resource.id}`
  }
  if (isAPIDetail(resource)) {
    return `/apimanager/${orgDomain}/#/organizations/${orgId}/environments/${envId}/apis/${resource.id}/api-settings`
  }
  if (isTarget(resource)) {
    const targetType = resource.type === TargetType.SERVER_GROUP ? 'serverGroup' : resource.type.toLowerCase()
    return `/cloudhub/#/console/servers/${targetType}/${resource.id}/settings`
  }
  return ''
}

export const goToLogPoints = (envId: string, resource: Resource, versionId?: string) => {
  const resourceId = getResourceId(resource)

  if (isAsset(resource) && versionId) window.location.hash = `${Urls.logPoints}/environments/${envId}/apis/${resourceId}/versions/${versionId}`
  else {
    const targetId = isARMHybridDetail(resource) ? `/${resource.target.id}` : ''
    window.location.hash = `${Urls.logPoints}/environments/${envId}/applications/${resourceId}${targetId}`
  }
}

export const goToRawData = (envId: string, resourceType: string, resourceId: string, date: string) => {
  window.location.hash = `${Urls.rawData}/environments/${envId}/${resourceType}/${resourceId}?date=${date}`
}

export default Urls
